/**
 * YTD Metrics Engine
 * Calculates Year-to-Date averages, variance, and auto-determines status
 */

class YTDMetricsEngine {
  /**
   * Calculate YTD metrics for all entities
   * @param {Array} entities - All entities (metrics, CSFs, etc.)
   * @param {Array} signals - All signals (daily values, scores, etc.)
   * @returns {Array} Enhanced entities with YTD calculations
   */
  calculateYTDMetrics(entities, signals) {
    const currentYear = new Date().getFullYear();

    return entities.map((entity) => {
      // Get all signals for this entity in current year
      const entitySignals = signals.filter((signal) => {
        const signalYear = new Date(signal.Timestamp).getFullYear();
        return (
          (signal.EntityIdLookupId == entity.Id ||
            signal.EntityIdLookupId == entity.Id) &&
          signalYear === currentYear &&
          signal.SignalType === "Value"
        );
      });

      if (entitySignals.length === 0) {
        return {
          ...entity,
          ytdMetrics: null,
        };
      }

      // Calculate YTD average
      const values = entitySignals
        .map((s) => s.NumericValue)
        .filter((v) => v != null);
      const ytdAverage =
        values.length > 0
          ? values.reduce((sum, val) => sum + val, 0) / values.length
          : 0;

      // Get target
      const target = entity.TargetValue || 0;

      // Calculate variance
      const variance = ytdAverage - target;
      const variancePercent = target !== 0 ? (variance / target) * 100 : 0;

      // Auto-determine status
      let status = "Not Met";
      let statusColor = "red";
      let trend = "down";

      if (ytdAverage >= target) {
        status = "Met";
        statusColor = "green";
        trend = "up";
      } else if (ytdAverage >= target * 0.9) {
        // Within 10% of target
        status = "At Risk";
        statusColor = "yellow";
        trend = "neutral";
      }

      // Calculate latest vs previous period
      const sortedSignals = entitySignals.sort(
        (a, b) => new Date(b.Timestamp) - new Date(a.Timestamp),
      );

      const latestValue =
        sortedSignals.length > 0 ? sortedSignals[0].NumericValue : 0;
      const previousValue =
        sortedSignals.length > 1 ? sortedSignals[1].NumericValue : latestValue;
      const recentTrend = latestValue >= previousValue ? "up" : "down";

      return {
        ...entity,
        ytdMetrics: {
          ytdAverage: Math.round(ytdAverage * 100) / 100,
          target,
          variance: Math.round(variance * 100) / 100,
          variancePercent: Math.round(variancePercent * 100) / 100,
          status,
          statusColor,
          trend,
          latestValue,
          dataPoints: values.length,
          unit: entity.TargetUnit || "",
          recentTrend,
          // Period information
          startDate:
            entitySignals.length > 0
              ? new Date(
                  Math.min(...entitySignals.map((s) => new Date(s.Timestamp))),
                )
              : null,
          endDate:
            entitySignals.length > 0
              ? new Date(
                  Math.max(...entitySignals.map((s) => new Date(s.Timestamp))),
                )
              : null,
        },
      };
    });
  }

  /**
   * Calculate monthly averages for trend analysis
   */
  calculateMonthlyAverages(entity, signals) {
    const currentYear = new Date().getFullYear();
    const monthlyData = {};

    // Get signals for this entity in current year
    const entitySignals = signals.filter((signal) => {
      const signalYear = new Date(signal.Timestamp).getFullYear();
      return (
        (signal.EntityIdLookupId == entity.Id ||
          signal.EntityIdLookupId == entity.Id) &&
        signalYear === currentYear &&
        signal.SignalType === "Value"
      );
    });

    // Group by month
    entitySignals.forEach((signal) => {
      const month = new Date(signal.Timestamp).getMonth(); // 0-11
      if (!monthlyData[month]) {
        monthlyData[month] = [];
      }
      monthlyData[month].push(signal.NumericValue);
    });

    // Calculate averages
    const monthlyAverages = {};
    Object.keys(monthlyData).forEach((month) => {
      const values = monthlyData[month];
      monthlyAverages[month] =
        values.reduce((sum, val) => sum + val, 0) / values.length;
    });

    return monthlyAverages;
  }

  /**
   * Get metrics summary for dashboard
   */
  getMetricsSummary(entitiesWithYTD) {
    const metrics = entitiesWithYTD.filter(
      (e) => e.EntityType === "Metric" && e.ytdMetrics,
    );

    const totalMetrics = metrics.length;
    const metMetrics = metrics.filter(
      (m) => m.ytdMetrics.status === "Met",
    ).length;
    const atRiskMetrics = metrics.filter(
      (m) => m.ytdMetrics.status === "At Risk",
    ).length;
    const notMetMetrics = metrics.filter(
      (m) => m.ytdMetrics.status === "Not Met",
    ).length;

    const performanceRate =
      totalMetrics > 0 ? Math.round((metMetrics / totalMetrics) * 100) : 0;

    return {
      totalMetrics,
      metMetrics,
      atRiskMetrics,
      notMetMetrics,
      performanceRate,
      status:
        performanceRate >= 80
          ? "Excellent"
          : performanceRate >= 60
            ? "Good"
            : performanceRate >= 40
              ? "Fair"
              : "Poor",
    };
  }

  /**
   * Format number with commas
   */
  formatNumber(num, decimals = 0) {
    if (num == null) return "0";
    return num.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  /**
   * Get trend icon
   */
  getTrendIcon(trend) {
    return trend === "up" ? "↑" : trend === "down" ? "↓" : "→";
  }

  /**
   * Get status badge color
   */
  getStatusColor(status) {
    const colors = {
      Met: "#107C10",
      "At Risk": "#FFB900",
      "Not Met": "#E81123",
      "On Track": "#0078D4",
      Delayed: "#D83B01",
      Blocked: "#5C2D91",
    };
    return colors[status] || "#666666";
  }
}

export default new YTDMetricsEngine();
